import BaseHttp from "@/api/httpHelper";
import apiGwClient from "@/api/api.gateway";
import type {
  IExpense,
  IExpenseResponse,
  ISubExpenseResponse,
  ISubExpense,
  IInvoicesResponse,
} from "@/types/response";
import type {
  IExpenseAmountAdd,
  IExpenseCreate,
  ISubExpenseCreate,
  IUpdateSubExpense,
  IUpdateExpense,
  IAddSubExpenseInvoice,
  IPayInvoice,
  IPaySubExpense,
} from "@/types/requests";

class ExpenseService extends BaseHttp {
  async getExpenses(pageNumber: number, pageSize: number, searchTerm: string) {
    const { data } = await this.get<IExpenseResponse>(
      "",
      { pageNumber, pageSize, searchTerm },
      { requestKey: "getExpenses" }
    );
    return data;
  }

  async createExpense(body: IExpenseCreate) {
    const { success, error } = await this.post<string>("", body, {
      requestKey: "createExpense",
    });
    return { success, error };
  }

  async getExpenseById(id: string) {
    const { data } = await this.get<{ expense: IExpense }>(
      `/${id}`,
      {},
      { requestKey: "getExpenseById" }
    );
    return data;
  }

  async deleteExpense(id: string) {
    const { success, error } = await this.delete<string>(
      `/${id}`,
      {},
      { requestKey: "deleteExpense" }
    );
    return { success, error };
  }

  async createSubExpense(id: string, body: ISubExpenseCreate) {
    const { data, success, error } = await this.post<string>(
      `/${id}/sub-expenses`,
      body,
      {
        requestKey: "createSubExpense",
      }
    );
    return { data, success, error };
  }

  async addExpenseAmount(id: string, body: IExpenseAmountAdd) {
    const { data, success, error } = await this.post<string>(
      `/${id}/pay`,
      body,
      {
        requestKey: "addExpenseAmount",
      }
    );
    return { data, success, error };
  }

  async deleteSubExpense(id: string, subId: string) {
    const { data, success, error } = await this.delete<string>(
      `/${id}/sub-expenses/${subId}/`,
      {
        requestKey: "deleteSubExpense",
      }
    );
    return { data, success, error };
  }

  async getSubExpenses(id: string, pageNumber: number, pageSize: number) {
    const { data } = await this.get<ISubExpenseResponse>(
      `/${id}/sub-expenses`,
      { pageNumber, pageSize },
      { requestKey: "getSubExpenses" }
    );
    return data;
  }

  async updateSubExpense(
    id: string,
    subExpenseId: string,
    body: IUpdateSubExpense
  ) {
    const { data, success, error } = await this.put<string>(
      `/${id}/sub-expenses/${subExpenseId}`,
      body,
      {
        requestKey: "updateSubExpense",
      }
    );
    return { data, success, error };
  }

  async addSubExpenseInvoice(
    id: string,
    subExpenseId: string,
    body: IAddSubExpenseInvoice
  ) {
    const { data, success, error } = await this.post<string>(
      `/${id}/sub-expenses/${subExpenseId}/invoices`,
      body,
      {
        requestKey: "addSubExpenseInvoice",
      }
    );
    return { data, success, error };
  }

  async updateExpense(id: string, body: IUpdateExpense) {
    const { data, success, error } = await this.put<string>(`/${id}`, body, {
      requestKey: "updateExpense",
    });
    return { data, success, error };
  }

  async getSubExpense(expenseId: string, subExpenseId: string) {
    const { data } = await this.get<{ subExpense: ISubExpense }>(
      `/${expenseId}/sub-expenses/${subExpenseId}`,
      {},
      { requestKey: "getSubExpense" }
    );
    return data;
  }

  async getInvoices(
    expenseId: string,
    subExpenseId: string,
    pageNumber: number,
    pageSize: number
  ) {
    const { data } = await this.get<IInvoicesResponse>(
      `/${expenseId}/sub-expenses/${subExpenseId}/invoices`,
      { pageNumber, pageSize },
      { requestKey: "getInvoices" }
    );
    return data;
  }

  async deleteSubExpenseInvoice(id: string, subId: string, invoiceId: string) {
    const { data, success, error } = await this.delete<string>(
      `/${id}/sub-expenses/${subId}/invoices/${invoiceId}`,
      {
        requestKey: "deleteSubExpenseInvoice",
      }
    );
    return { data, success, error };
  }

  async payInvoice(
    id: string,
    subId: string,
    invoiceId: string,
    body: IPayInvoice
  ) {
    const { data, success, error } = await this.post<string>(
      `/${id}/sub-expenses/${subId}/invoices/${invoiceId}/pay`,
      body,
      {
        requestKey: "payInvoice",
      }
    );
    return { data, success, error };
  }

  async paySubExpense(id: string, subExpenseId: string, body: IPaySubExpense) {
    const { data, success, error } = await this.post<string>(
      `/${id}/sub-expenses/${subExpenseId}/pay`,
      body,
      {
        requestKey: "paySubExpense",
      }
    );
    return { data, success, error };
  }
}

export default new ExpenseService(apiGwClient, "/api/v1/expenses");
